import { useState, useEffect } from "react";

const formatDate = (d) => {
  if (!d) return "";
  return new Date(d).toLocaleDateString("nl-NL");
};

const today = () => new Date().toISOString().split("T")[0];
const nowTime = () => new Date().toTimeString().slice(0, 5);
const genId = () => Math.random().toString(36).substr(2, 9);

const apiFetch = async (path, opts = {}) => {
  const password = sessionStorage.getItem("app_password") || "";
  const headers = new Headers(opts.headers || {});
  if (password) headers.set("authorization", `Bearer ${password}`);
  const res = await fetch(path, { ...opts, headers });

  if (res.status === 401) {
    const err = new Error("unauthorized");
    err.code = 401;
    throw err;
  }

  if (!res.ok) {
    const err = new Error(`request_failed_${res.status}`);
    err.code = res.status;
    throw err;
  }

  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) return res.json();
  return res.text();
};

export default function Ritten() {
  const [view, setView] = useState("lijst");
  const [ritten, setRitten] = useState([]);
  const [auto, setAuto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedRit, setSelectedRit] = useState(null);
  const [filter, setFilter] = useState("alle");
  const [editingRitId, setEditingRitId] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");

  const [form, setForm] = useState({
    datum: today(), werktijd_van: nowTime(), werktijd_tot: "",
    begin_km: "", eind_km: "", vertrek_adres: "", aankomst_adres: "",
    type_rit: "Zakelijk", notities: "",
  });

  const [autoForm, setAutoForm] = useState({ merk: "", type_auto: "", kenteken: "" });

  useEffect(() => {
    const boot = async () => {
      const existing = sessionStorage.getItem("app_password") || "";
      if (!existing) {
        setAuthReady(false);
        return;
      }

      try {
        const [tripsData, settingsData] = await Promise.all([
          apiFetch("/api/trips"),
          apiFetch("/api/settings"),
        ]);
        const trips = (tripsData?.trips || []).sort((a, b) => b.datum.localeCompare(a.datum));
        setRitten(trips);
        if (settingsData?.auto) {
          setAuto(settingsData.auto);
          setAutoForm(settingsData.auto);
        }
        setAuthReady(true);
      } catch (e) {
        sessionStorage.removeItem("app_password");
        setAuthReady(false);
      }
    };

    boot();
  }, []);

  const login = async () => {
    sessionStorage.setItem("app_password", passwordInput);
    try {
      const [tripsData, settingsData] = await Promise.all([
        apiFetch("/api/trips"),
        apiFetch("/api/settings"),
      ]);
      const trips = (tripsData?.trips || []).sort((a, b) => b.datum.localeCompare(a.datum));
      setRitten(trips);
      if (settingsData?.auto) {
        setAuto(settingsData.auto);
        setAutoForm(settingsData.auto);
      }
      setAuthReady(true);
      setPasswordInput("");
    } catch (e) {
      sessionStorage.removeItem("app_password");
      alert("Wachtwoord onjuist.");
      setAuthReady(false);
    }
  };

  const saveRit = () => {
    if (!form.datum || !form.begin_km || !form.eind_km || !form.vertrek_adres || !form.aankomst_adres) {
      alert("Vul alle verplichte velden in."); return;
    }
    const run = async () => {
      setSaving(true);
      const normalized = { ...form, begin_km: parseFloat(form.begin_km), eind_km: parseFloat(form.eind_km) };

      try {
        if (editingRitId) {
          await apiFetch(`/api/trips/${editingRitId}`, {
            method: "PUT",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(normalized),
          });
        } else {
          const newId = genId();
          await apiFetch("/api/trips", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ ...normalized, id: newId }),
          });
        }

        const tripsData = await apiFetch("/api/trips");
        const trips = (tripsData?.trips || []).sort((a, b) => b.datum.localeCompare(a.datum));
        setRitten(trips);
        if (editingRitId) {
          const updatedSelected = trips.find(r => r.id === editingRitId) || null;
          setSelectedRit(updatedSelected);
        }
        setEditingRitId(null);
        setForm({ datum: today(), werktijd_van: nowTime(), werktijd_tot: "", begin_km: "", eind_km: "", vertrek_adres: "", aankomst_adres: "", type_rit: "Zakelijk", notities: "" });
        setSaving(false);
        setView(editingRitId ? "detail" : "lijst");
      } catch (e) {
        setSaving(false);
        if (e?.code === 401) {
          sessionStorage.removeItem("app_password");
          setAuthReady(false);
          return;
        }
        alert("Opslaan mislukt.");
      }
    };

    run();
  };

  const startEditRit = (rit) => {
    setSelectedRit(rit);
    setEditingRitId(rit.id);
    setForm({
      datum: rit.datum || today(),
      werktijd_van: rit.werktijd_van || "",
      werktijd_tot: rit.werktijd_tot || "",
      begin_km: (rit.begin_km ?? "").toString(),
      eind_km: (rit.eind_km ?? "").toString(),
      vertrek_adres: rit.vertrek_adres || "",
      aankomst_adres: rit.aankomst_adres || "",
      type_rit: rit.type_rit || "Zakelijk",
      notities: rit.notities || "",
    });
    setView("bewerk");
  };

  const saveAuto = () => {
    const run = async () => {
      setSaving(true);
      try {
        await apiFetch("/api/settings", {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(autoForm),
        });
        setAuto(autoForm);
        setSaving(false);
        setView("lijst");
      } catch (e) {
        setSaving(false);
        if (e?.code === 401) {
          sessionStorage.removeItem("app_password");
          setAuthReady(false);
          return;
        }
        alert("Opslaan mislukt.");
      }
    };

    run();
  };

  const deleteRit = (id) => {
    if (!window.confirm("Rit verwijderen?")) return;
    const run = async () => {
      try {
        await apiFetch(`/api/trips/${id}`, { method: "DELETE" });
        const tripsData = await apiFetch("/api/trips");
        const trips = (tripsData?.trips || []).sort((a, b) => b.datum.localeCompare(a.datum));
        setRitten(trips);
        setSelectedRit(null);
        setView("lijst");
      } catch (e) {
        if (e?.code === 401) {
          sessionStorage.removeItem("app_password");
          setAuthReady(false);
          return;
        }
        alert("Verwijderen mislukt.");
      }
    };

    run();
  };

  const filteredRitten = ritten.filter(r => filter === "alle" || r.type_rit === filter);
  const totalKm = filteredRitten.reduce((sum, r) => sum + ((r.eind_km || 0) - (r.begin_km || 0)), 0);

  const exportCSV = () => {
    const rows = [
      ["Merk", auto?.merk || "", "Type", auto?.type_auto || "", "Kenteken", auto?.kenteken || ""],
      [],
      ["Datum", "Werktijd van", "Werktijd tot", "Begin km", "Eind km", "Gereden km", "Vertrek", "Aankomst", "Zakelijk/Privé", "Notities"],
      ...ritten.map(r => [r.datum, r.werktijd_van || "", r.werktijd_tot || "", r.begin_km || "", r.eind_km || "", (r.eind_km || 0) - (r.begin_km || 0), r.vertrek_adres || "", r.aankomst_adres || "", r.type_rit || "", r.notities || ""]),
    ];
    const csv = rows.map(r => r.join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "rittenregistratie.csv"; a.click();
  };

  if (!authReady) {
    return (
      <div style={s.app}>
        <div style={s.header}>
          <div>
            <div style={s.headerTitle}>🚗 Rittenregistratie</div>
          </div>
        </div>
        <div style={s.content}>
          <div style={s.form}>
            <label style={s.label}>Wachtwoord</label>
            <input style={s.input} type="password" value={passwordInput} onChange={e => setPasswordInput(e.target.value)} />
            <button style={s.saveBtn} onClick={login} disabled={saving || !passwordInput}>{saving ? "Controleren..." : "✅ Inloggen"}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={s.app}>
      <div style={s.header}>
        <div>
          <div style={s.headerTitle}>🚗 Rittenregistratie</div>
          {auto && <div style={s.headerSub}>{auto.merk} {auto.type_auto} · {auto.kenteken}</div>}
        </div>
        <button style={s.iconBtn} onClick={() => setView("instellingen")}>⚙️</button>
      </div>

      {view === "lijst" && (
        <div style={s.content}>
          <div style={s.statsRow}>
            <div style={s.statCard}><div style={s.statNum}>{ritten.length}</div><div style={s.statLabel}>Ritten totaal</div></div>
            <div style={s.statCard}><div style={s.statNum}>{ritten.filter(r => r.type_rit === "Zakelijk").reduce((sum, r) => sum + ((r.eind_km||0)-(r.begin_km||0)), 0)}</div><div style={s.statLabel}>Zakelijke km</div></div>
            <div style={s.statCard}><div style={s.statNum}>{ritten.filter(r => r.type_rit === "Privé").reduce((sum, r) => sum + ((r.eind_km||0)-(r.begin_km||0)), 0)}</div><div style={s.statLabel}>Privé km</div></div>
          </div>
          <div style={s.filterRow}>
            {["alle", "Zakelijk", "Privé"].map(f => (
              <button key={f} style={{ ...s.filterBtn, ...(filter === f ? s.filterActive : {}) }} onClick={() => setFilter(f)}>{f === "alle" ? "Alle" : f}</button>
            ))}
            <button style={s.exportBtn} onClick={exportCSV}>📥 Export</button>
          </div>
          {filteredRitten.length === 0 ? (
            <div style={s.empty}>
              <div style={{ fontSize: 48 }}>🚗</div>
              <p>Nog geen ritten geregistreerd.</p>
            </div>
          ) : (
            <div style={s.list}>
              {filteredRitten.map(rit => (
                <div key={rit.id} style={s.ritCard} onClick={() => { setSelectedRit(rit); setView("detail"); }}>
                  <div style={s.ritTop}>
                    <span style={s.ritDatum}>{formatDate(rit.datum)}</span>
                    <span style={{ ...s.badge, ...(rit.type_rit === "Zakelijk" ? s.badgeZ : s.badgeP) }}>{rit.type_rit}</span>
                  </div>
                  <div style={s.ritRoute}>{rit.vertrek_adres} → {rit.aankomst_adres}</div>
                  <div style={s.ritKm}>
                    <span>📍 {(rit.eind_km||0)-(rit.begin_km||0)} km</span>
                    {rit.werktijd_van && <span style={{ color: "#aaa", fontSize: 13 }}>{rit.werktijd_van}{rit.werktijd_tot ? ` - ${rit.werktijd_tot}` : ""}</span>}
                  </div>
                </div>
              ))}
              <div style={s.totalRow}>Totaal: <strong>{totalKm} km</strong></div>
            </div>
          )}
          <button style={s.fab} onClick={() => setView("nieuw")}>+ Nieuwe rit toevoegen</button>
        </div>
      )}

      {view === "nieuw" && (
        <div style={s.content}>
          <div style={s.formHeader}>
            <button style={s.backBtn} onClick={() => setView("lijst")}>← Terug</button>
            <h2 style={s.formTitle}>Nieuwe rit</h2>
          </div>
          <div style={s.form}>
            <label style={s.label}>Datum *</label>
            <input style={s.input} type="date" value={form.datum} onChange={e => setForm({...form, datum: e.target.value})} />
            <div style={s.row2}>
              <div style={{ flex: 1 }}><label style={s.label}>Werktijd van</label><input style={s.input} type="time" value={form.werktijd_van} onChange={e => setForm({...form, werktijd_van: e.target.value})} /></div>
              <div style={{ flex: 1 }}><label style={s.label}>Werktijd tot</label><input style={s.input} type="time" value={form.werktijd_tot} onChange={e => setForm({...form, werktijd_tot: e.target.value})} /></div>
            </div>
            <div style={s.row2}>
              <div style={{ flex: 1 }}><label style={s.label}>Begin km *</label><input style={s.input} type="number" placeholder="bv. 45230" value={form.begin_km} onChange={e => setForm({...form, begin_km: e.target.value})} /></div>
              <div style={{ flex: 1 }}><label style={s.label}>Eind km *</label><input style={s.input} type="number" placeholder="bv. 45285" value={form.eind_km} onChange={e => setForm({...form, eind_km: e.target.value})} /></div>
            </div>
            {form.begin_km && form.eind_km && parseFloat(form.eind_km) > parseFloat(form.begin_km) && (
              <div style={s.kmPreview}>✅ {parseFloat(form.eind_km) - parseFloat(form.begin_km)} km gereden</div>
            )}
            <label style={s.label}>Vertrek adres *</label>
            <input style={s.input} type="text" placeholder="bv. Bedrijfsstraat 1, Doetinchem" value={form.vertrek_adres} onChange={e => setForm({...form, vertrek_adres: e.target.value})} />
            <label style={s.label}>Aankomst adres *</label>
            <input style={s.input} type="text" placeholder="bv. Klantstraat 5, Zutphen" value={form.aankomst_adres} onChange={e => setForm({...form, aankomst_adres: e.target.value})} />
            <label style={s.label}>Type rit</label>
            <div style={s.toggleRow}>
              {["Zakelijk", "Privé"].map(t => (
                <button key={t} style={{ ...s.toggleBtn, ...(form.type_rit === t ? s.toggleActive : {}) }} onClick={() => setForm({...form, type_rit: t})}>
                  {t === "Zakelijk" ? "💼 Zakelijk" : "🏠 Privé"}
                </button>
              ))}
            </div>
            <label style={s.label}>Notities</label>
            <textarea style={s.textarea} placeholder="Optioneel..." value={form.notities} onChange={e => setForm({...form, notities: e.target.value})} />
            <button style={s.saveBtn} onClick={saveRit} disabled={saving}>{saving ? "Opslaan..." : "✅ Rit opslaan"}</button>
          </div>
        </div>
      )}

      {(view === "bewerk") && (
        <div style={s.content}>
          <div style={s.formHeader}>
            <button style={s.backBtn} onClick={() => { setEditingRitId(null); setView("detail"); }}>← Terug</button>
            <h2 style={s.formTitle}>Rit bewerken</h2>
          </div>
          <div style={s.form}>
            <label style={s.label}>Datum *</label>
            <input style={s.input} type="date" value={form.datum} onChange={e => setForm({...form, datum: e.target.value})} />
            <div style={s.row2}>
              <div style={{ flex: 1 }}><label style={s.label}>Werktijd van</label><input style={s.input} type="time" value={form.werktijd_van} onChange={e => setForm({...form, werktijd_van: e.target.value})} /></div>
              <div style={{ flex: 1 }}><label style={s.label}>Werktijd tot</label><input style={s.input} type="time" value={form.werktijd_tot} onChange={e => setForm({...form, werktijd_tot: e.target.value})} /></div>
            </div>
            <div style={s.row2}>
              <div style={{ flex: 1 }}><label style={s.label}>Begin km *</label><input style={s.input} type="number" placeholder="bv. 45230" value={form.begin_km} onChange={e => setForm({...form, begin_km: e.target.value})} /></div>
              <div style={{ flex: 1 }}><label style={s.label}>Eind km *</label><input style={s.input} type="number" placeholder="bv. 45285" value={form.eind_km} onChange={e => setForm({...form, eind_km: e.target.value})} /></div>
            </div>
            {form.begin_km && form.eind_km && parseFloat(form.eind_km) > parseFloat(form.begin_km) && (
              <div style={s.kmPreview}>✅ {parseFloat(form.eind_km) - parseFloat(form.begin_km)} km gereden</div>
            )}
            <label style={s.label}>Vertrek adres *</label>
            <input style={s.input} type="text" placeholder="bv. Bedrijfsstraat 1, Doetinchem" value={form.vertrek_adres} onChange={e => setForm({...form, vertrek_adres: e.target.value})} />
            <label style={s.label}>Aankomst adres *</label>
            <input style={s.input} type="text" placeholder="bv. Klantstraat 5, Zutphen" value={form.aankomst_adres} onChange={e => setForm({...form, aankomst_adres: e.target.value})} />
            <label style={s.label}>Type rit</label>
            <div style={s.toggleRow}>
              {["Zakelijk", "Privé"].map(t => (
                <button key={t} style={{ ...s.toggleBtn, ...(form.type_rit === t ? s.toggleActive : {}) }} onClick={() => setForm({...form, type_rit: t})}>
                  {t === "Zakelijk" ? "💼 Zakelijk" : "🏠 Privé"}
                </button>
              ))}
            </div>
            <label style={s.label}>Notities</label>
            <textarea style={s.textarea} placeholder="Optioneel..." value={form.notities} onChange={e => setForm({...form, notities: e.target.value})} />
            <button style={s.saveBtn} onClick={saveRit} disabled={saving}>{saving ? "Opslaan..." : "✅ Wijzigingen opslaan"}</button>
          </div>
        </div>
      )}

      {view === "detail" && selectedRit && (
        <div style={s.content}>
          <div style={s.formHeader}>
            <button style={s.backBtn} onClick={() => setView("lijst")}>← Terug</button>
            <h2 style={s.formTitle}>Rit details</h2>
          </div>
          <div style={s.detailCard}>
            {[
              ["Datum", formatDate(selectedRit.datum)],
              ["Werktijd", `${selectedRit.werktijd_van || "-"}${selectedRit.werktijd_tot ? ` - ${selectedRit.werktijd_tot}` : ""}`],
              ["Begin km", selectedRit.begin_km],
              ["Eind km", selectedRit.eind_km],
              ["Gereden", `${(selectedRit.eind_km||0)-(selectedRit.begin_km||0)} km`],
              ["Vertrek", selectedRit.vertrek_adres],
              ["Aankomst", selectedRit.aankomst_adres],
              ["Type", selectedRit.type_rit],
              ...(selectedRit.notities ? [["Notities", selectedRit.notities]] : []),
            ].map(([label, val]) => (
              <div key={label} style={s.detailRow}>
                <span style={s.detailLabel}>{label}</span>
                <span>{val}</span>
              </div>
            ))}
          </div>
          <button style={s.saveBtn} onClick={() => startEditRit(selectedRit)}>✏️ Rit bewerken</button>
          <button style={s.deleteBtn} onClick={() => deleteRit(selectedRit.id)}>🗑️ Rit verwijderen</button>
        </div>
      )}

      {view === "instellingen" && (
        <div style={s.content}>
          <div style={s.formHeader}>
            <button style={s.backBtn} onClick={() => setView("lijst")}>← Terug</button>
            <h2 style={s.formTitle}>Auto instellingen</h2>
          </div>
          <div style={s.form}>
            <label style={s.label}>Merk</label>
            <input style={s.input} type="text" placeholder="bv. Volkswagen" value={autoForm.merk} onChange={e => setAutoForm({...autoForm, merk: e.target.value})} />
            <label style={s.label}>Type</label>
            <input style={s.input} type="text" placeholder="bv. Golf 1.4 TSI" value={autoForm.type_auto} onChange={e => setAutoForm({...autoForm, type_auto: e.target.value})} />
            <label style={s.label}>Kenteken</label>
            <input style={s.input} type="text" placeholder="bv. AB-123-C" value={autoForm.kenteken} onChange={e => setAutoForm({...autoForm, kenteken: e.target.value})} />
            <button style={s.saveBtn} onClick={saveAuto} disabled={saving}>{saving ? "Opslaan..." : "✅ Opslaan"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  app: { maxWidth: 480, margin: "0 auto", minHeight: "100vh", background: "#f5f5f5", fontFamily: "'Inter', sans-serif" },
  header: { background: "#1a1a2e", color: "#fff", padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, zIndex: 10 },
  headerTitle: { fontSize: 20, fontWeight: 700 },
  headerSub: { fontSize: 13, color: "#aaa", marginTop: 2 },
  iconBtn: { background: "none", border: "none", fontSize: 22, cursor: "pointer" },
  content: { padding: "16px 16px 80px" },
  statsRow: { display: "flex", gap: 10, marginBottom: 16 },
  statCard: { flex: 1, background: "#fff", borderRadius: 12, padding: "14px 10px", textAlign: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.07)" },
  statNum: { fontSize: 24, fontWeight: 700, color: "#1a1a2e" },
  statLabel: { fontSize: 11, color: "#888", marginTop: 2 },
  filterRow: { display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" },
  filterBtn: { padding: "6px 14px", borderRadius: 20, border: "1px solid #ddd", background: "#fff", fontSize: 13, cursor: "pointer" },
  filterActive: { background: "#1a1a2e", color: "#fff", border: "1px solid #1a1a2e" },
  exportBtn: { marginLeft: "auto", padding: "6px 14px", borderRadius: 20, border: "1px solid #1a1a2e", background: "#fff", fontSize: 13, cursor: "pointer", color: "#1a1a2e" },
  list: { display: "flex", flexDirection: "column", gap: 10 },
  ritCard: { background: "#fff", borderRadius: 12, padding: 14, boxShadow: "0 1px 4px rgba(0,0,0,0.07)", cursor: "pointer" },
  ritTop: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 },
  ritDatum: { fontWeight: 600, color: "#333" },
  badge: { fontSize: 11, padding: "3px 8px", borderRadius: 10, fontWeight: 600 },
  badgeZ: { background: "#e8f4fd", color: "#1a73e8" },
  badgeP: { background: "#fef3e2", color: "#e67e22" },
  ritRoute: { fontSize: 14, color: "#555", marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  ritKm: { fontSize: 13, color: "#666", display: "flex", gap: 12 },
  totalRow: { textAlign: "right", color: "#888", fontSize: 13, marginTop: 8 },
  empty: { textAlign: "center", padding: "60px 20px", color: "#666" },
  fab: { display: "block", width: "100%", padding: "16px", marginTop: 16, background: "#1a1a2e", color: "#fff", fontSize: 18, fontWeight: 700, border: "none", borderRadius: 14, cursor: "pointer", boxShadow: "0 4px 12px rgba(0,0,0,0.2)" },
  formHeader: { display: "flex", alignItems: "center", gap: 12, marginBottom: 20 },
  backBtn: { background: "none", border: "none", fontSize: 16, cursor: "pointer", color: "#1a1a2e", fontWeight: 600 },
  formTitle: { fontSize: 20, fontWeight: 700, margin: 0, color: "#1a1a2e" },
  form: { display: "flex", flexDirection: "column", gap: 6, background: "#fff", padding: 16, borderRadius: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.07)" },
  label: { fontSize: 12, fontWeight: 600, color: "#666", marginTop: 8, textTransform: "uppercase", letterSpacing: "0.5px" },
  input: { padding: "12px 14px", borderRadius: 10, border: "1.5px solid #e0e0e0", fontSize: 16, outline: "none", background: "#fafafa", width: "100%", boxSizing: "border-box" },
  textarea: { padding: "12px 14px", borderRadius: 10, border: "1.5px solid #e0e0e0", fontSize: 14, outline: "none", background: "#fafafa", minHeight: 80, resize: "vertical", width: "100%", boxSizing: "border-box" },
  row2: { display: "flex", gap: 10 },
  kmPreview: { background: "#e8f5e9", color: "#2e7d32", padding: "10px 14px", borderRadius: 10, fontWeight: 600, fontSize: 15 },
  toggleRow: { display: "flex", gap: 10, marginTop: 4 },
  toggleBtn: { flex: 1, padding: "12px", borderRadius: 10, border: "1.5px solid #e0e0e0", background: "#fafafa", fontSize: 15, cursor: "pointer", fontWeight: 500 },
  toggleActive: { background: "#1a1a2e", color: "#fff", border: "1.5px solid #1a1a2e" },
  saveBtn: { marginTop: 16, padding: "14px", background: "#1a1a2e", color: "#fff", border: "none", borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: "pointer" },
  detailCard: { background: "#fff", borderRadius: 16, padding: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.07)" },
  detailRow: { display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #f0f0f0", fontSize: 15 },
  detailLabel: { color: "#888", fontWeight: 500 },
  deleteBtn: { marginTop: 16, width: "100%", padding: 14, background: "#fff", color: "#e74c3c", border: "1.5px solid #e74c3c", borderRadius: 12, fontSize: 15, cursor: "pointer", fontWeight: 600 },
};
